import { getClientTimezone } from './functions.tsx';
import { LeagueModel, MarketModel } from '../models/Sport.tsx';
import { Schedule } from '../models/Schedule.tsx';

/** In-progress only. Covers MLB extras, long ATP, and a short delay. */
const LIVE_LOOKBACK_MS = 8 * 60 * 60 * 1000;
/** Fetch the next slate so it is ready when the current day ends. */
const LIVE_LOOKAHEAD_MS = 8 * 60 * 60 * 1000;
/** US sports calendar day. UTC would split a Saturday afternoon/night slate. */
const LIVE_SLATE_TIMEZONE = 'America/New_York';

/** Pregame league/market fetches: start of day plus the next 3 calendar days. */
export const PREGAME_SCHEDULE_EXTRA_DAYS = 3;

export type ScheduleDateRangeOptions = {
  /** Calendar days beyond the start day. Default 0 = same day only. */
  extraDays?: number;
  /** Calendar days before the base date. Default 0. */
  lookbackDays?: number;
  /** Anchor instant. Defaults to now. */
  baseDate?: Date;
  /** IANA timezone for the calendar day and offset. Defaults to the client. */
  timeZone?: string;
  /** When useDateRange is true and from/to are set, use that window. */
  league?: LeagueModel;
  /** When useDateRange is true and from/to are set, market window takes priority over league. */
  market?: MarketModel;
};

export type ScheduleDateRange = {
  timeOffset: string;
  startDateAfter: string;
  startDateBefore: string;
};

function toDateKey(value: Date, timeZone?: string): string {
  return timeZone
    ? value.toLocaleDateString('en-CA', { timeZone })
    : value.toLocaleDateString('en-CA');
}

function shiftDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

/** Offset string like -06:00 for a timezone at the given instant. */
function getTimeOffset(date: Date, timeZone?: string): string {
  if (!timeZone) return getClientTimezone();

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const map: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }

  const wallAsUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  const offsetMinutes = Math.round((wallAsUtc - date.getTime()) / 60000);
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absolute = Math.abs(offsetMinutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Extract YYYY-MM-DD from ISO strings, Date, or Firestore timestamps. */
function parseDateKey(value?: unknown): string | null {
  if (value == null || value === "") return null;

  if (typeof value === "object") {
    const stamp = value as { toDate?: () => Date; seconds?: number };
    if (typeof stamp.toDate === "function") {
      const fromStamp = stamp.toDate();
      if (!Number.isNaN(fromStamp.getTime())) return toDateKey(fromStamp);
    }
    if (typeof stamp.seconds === "number") {
      return toDateKey(new Date(stamp.seconds * 1000));
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return toDateKey(value);
    }
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const isoDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDate) return isoDate[1];

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return toDateKey(parsed);
}

function buildRange(startKey: string, endKey: string, timeOffset: string): ScheduleDateRange {
  return {
    timeOffset,
    startDateAfter: `${startKey}T00:00:00${timeOffset}`,
    startDateBefore: `${endKey}T23:59:59${timeOffset}`,
  };
}

type DateRangeSource = {
  from?: unknown;
  to?: unknown;
  useDateRange?: boolean;
};

function hasConfiguredDates(source: DateRangeSource | undefined): boolean {
  if (!source?.useDateRange) return false;
  return Boolean(source.from || source.to);
}

function configuredWindow(
  source: DateRangeSource | undefined,
  todayKey: string,
  extraDays: number,
  timeOffset: string
): ScheduleDateRange | null {
  if (!hasConfiguredDates(source)) return null;

  const fromKey = parseDateKey(source?.from);
  const toKey = parseDateKey(source?.to);

  if (!fromKey && !toKey) return null;

  if (toKey) {
    if (toKey < todayKey) return null;
    const startKey = fromKey && fromKey > todayKey ? fromKey : todayKey;
    if (startKey > toKey) return null;
    return buildRange(startKey, toKey, timeOffset);
  }

  const startKey = fromKey && fromKey > todayKey ? fromKey : todayKey;
  return buildRange(startKey, shiftDateKey(startKey, extraDays), timeOffset);
}

export function findLeagueMarket(league?: LeagueModel, marketKey?: string): MarketModel | undefined {
  if (!league?.markets?.length || !marketKey) return undefined;
  return league.markets.find(
    market => market.name === marketKey || market.selections?.includes(marketKey)
  );
}

/**
 * On-demand schedule query window.
 * Call at fetch/poll time — do not cache at module load.
 *
 * If the market or league has useDateRange true with from and/or to, that window is used
 * until it ends. With only from, extraDays is the end. With to, to is the end.
 * If useDateRange is false, from/to are ignored and the lookbackDays/extraDays default applies.
 * If the window already passed, falls back to lookbackDays/extraDays from today.
 */
export function getScheduleDateRange(
  options: ScheduleDateRangeOptions = {}
): ScheduleDateRange {
  const {
    extraDays = 0,
    lookbackDays = 0,
    baseDate = new Date(),
    timeZone,
    league,
    market,
  } = options;
  const timeOffset = getTimeOffset(baseDate, timeZone);
  const todayKey = toDateKey(baseDate, timeZone);
  const configured =
    configuredWindow(market, todayKey, extraDays, timeOffset) ??
    configuredWindow(league, todayKey, extraDays, timeOffset);

  if (configured) return configured;

  return buildRange(
    shiftDateKey(todayKey, -lookbackDays),
    shiftDateKey(todayKey, extraDays),
    timeOffset
  );
}

/**
 * Configured from/to when useDateRange is true on the league or any of its markets.
 * Props needs this because some leagues only store the window on markets.
 * Returns null when there is no usable window (or it already ended).
 */
export function getLeagueConfiguredDateRange(
  league?: LeagueModel,
  options: Pick<ScheduleDateRangeOptions, "extraDays" | "baseDate" | "timeZone"> = {}
): ScheduleDateRange | null {
  if (!league) return null;

  const extraDays = options.extraDays ?? PREGAME_SCHEDULE_EXTRA_DAYS;
  const baseDate = options.baseDate ?? new Date();
  const timeZone = options.timeZone;
  const timeOffset = getTimeOffset(baseDate, timeZone);
  const todayKey = toDateKey(baseDate, timeZone);

  const windows = [
    configuredWindow(league, todayKey, extraDays, timeOffset),
    ...(league.markets ?? []).map(market =>
      configuredWindow(market, todayKey, extraDays, timeOffset)
    ),
  ].filter((range): range is ScheduleDateRange => range != null);

  if (windows.length === 0) return null;

  return {
    timeOffset,
    startDateAfter: windows.reduce(
      (min, range) => (range.startDateAfter < min ? range.startDateAfter : min),
      windows[0].startDateAfter
    ),
    startDateBefore: windows.reduce(
      (max, range) => (range.startDateBefore > max ? range.startDateBefore : max),
      windows[0].startDateBefore
    ),
  };
}

function toUtcOffsetDateTime(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, '+00:00');
}

/**
 * Same live window for every client: UTC now −12h / +12h.
 * Which day is shown is decided afterwards by pickLiveSlateGames.
 */
export function getLiveScheduleDateRange(baseDate = new Date()): ScheduleDateRange {
  const after = new Date(baseDate.getTime() - LIVE_LOOKBACK_MS);
  const before = new Date(baseDate.getTime() + LIVE_LOOKAHEAD_MS);
  return {
    timeOffset: '+00:00',
    startDateAfter: toUtcOffsetDateTime(after),
    startDateBefore: toUtcOffsetDateTime(before),
  };
}

export function isActiveScheduleGame(game: Schedule): boolean {
  return game.status !== 'completed' && game.status !== 'cancelled';
}

/**
 * Tournament round shells (PGA "Round 3", no golfers) are not matchups.
 * A real game always lists at least one competitor.
 */
export function isFixtureMatchup(game: {
  sport?: { id?: string | null; name?: string | null } | null;
  league?: { id?: string | null; name?: string | null } | null;
  home_competitors?: unknown[] | null;
  away_competitors?: unknown[] | null;
}): boolean {
  const home = game.home_competitors?.length ?? 0;
  const away = game.away_competitors?.length ?? 0;
  if (home > 0 || away > 0) return true;

  const sport = `${game.sport?.id ?? ""} ${game.sport?.name ?? ""}`.toLowerCase();
  const league = `${game.league?.id ?? ""} ${game.league?.name ?? ""}`.toLowerCase();
  const isGolf = sport.includes("golf") || league.includes("pga");
  return !isGolf;
}

/** Prematch props: still upcoming. Started, live, or finished games stay hidden. */
export function isUpcomingPropsGame(game: Schedule, now: Date = new Date()): boolean {
  if (!isActiveScheduleGame(game)) return false;
  if (game.is_live || String(game.status ?? '').toLowerCase() === 'live') return false;
  if (String(game.status ?? '').toLowerCase() !== 'unplayed') return false;
  const nowMs = now instanceof Date ? now.getTime() : Date.now();
  const start = new Date(game.start_date).getTime();
  return Number.isFinite(start) && start > nowMs;
}

function isCurrentlyLive(game: Schedule): boolean {
  return Boolean(game.is_live) || String(game.status ?? '').toLowerCase() === 'live';
}

function liveSlateDateKey(startDate?: string): string | null {
  if (!startDate) return null;
  const parsed = new Date(startDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return toDateKey(parsed, LIVE_SLATE_TIMEZONE);
}

/**
 * Per league: if any game is already live/started, keep that US sports day
 * so the next calendar day stays hidden until those games finish.
 * Games that are in play are always kept, even if kickoff crossed midnight UTC.
 */
export function pickLiveSlateGames(games: Schedule[], now = new Date()): Schedule[] {
  const active = games.filter(isActiveScheduleGame);
  if (active.length === 0) return [];

  const nowMs = now.getTime();
  const inPlay = active.filter(game => {
    if (isCurrentlyLive(game)) return true;
    const start = new Date(game.start_date).getTime();
    return Number.isFinite(start) && start <= nowMs;
  });

  if (inPlay.length === 0) return active;

  const currentDay = inPlay
    .map(game => liveSlateDateKey(game.start_date))
    .filter((key): key is string => Boolean(key))
    .sort()[0];

  if (!currentDay) return active;

  return active.filter(
    game => isCurrentlyLive(game) || liveSlateDateKey(game.start_date) === currentDay
  );
}

/** Prefer today's active fixtures; if none remain, show tomorrow's. */
export function pickPropsFixtures(games: Schedule[], baseDate = new Date()): Schedule[] {
  const todayKey = toDateKey(baseDate);
  const tomorrow = new Date(baseDate);
  tomorrow.setDate(baseDate.getDate() + 1);
  const tomorrowKey = toDateKey(tomorrow);

  const sortByStart = (a: Schedule, b: Schedule) =>
    new Date(a.start_date).getTime() - new Date(b.start_date).getTime();

  const activeGames = games.filter(game => isUpcomingPropsGame(game, baseDate));
  const todayGames = activeGames.filter(game => toDateKey(new Date(game.start_date)) === todayKey);

  if (todayGames.length > 0) {
    return todayGames.sort(sortByStart);
  }

  return activeGames
    .filter(game => toDateKey(new Date(game.start_date)) === tomorrowKey)
    .sort(sortByStart);
}
